import React, { useRef } from 'react';
import AssetWrapper, { AssetWrapperProps } from 'src/app/atoms/Asset/AssetWrapper';
import Stack from 'src/app/atoms/Layout/Stack/Stack';
import Typography, { TypographyProps } from 'src/app/atoms/Typography/Typography';
import { OverlayTextBlock, PlayButtonContainer, VideoContainer } from './MediaCard.styles';
import useOnHover from 'src/hooks/useOnHover';
import Clickable from 'src/app/atoms/Clickable/Clickable';
import PlayButtonIcon from '../PlayButtonIcon/PlayButtonIcon';
import { SpacingPropKeys } from 'src/app/styles/spacing';
import useOnDrag from 'src/hooks/useOnDrag';

export interface MediaCardProps {
  title?: TypographyProps;
  supportingText?: TypographyProps;
  textGap?: SpacingPropKeys;
  image: AssetWrapperProps['imageProps'];
  imageMaxWidth?: string | number;
  hoverVideo?: AssetWrapperProps['videoProps'];
  onClick?: () => void;
  showPlayIcon?: boolean;
}

const MediaCard = ({
  title,
  supportingText,
  textGap = 'spacing-3xl',
  image,
  imageMaxWidth,
  hoverVideo,
  onClick,
  showPlayIcon,
}: MediaCardProps): JSX.Element => {
  const ref = useRef<HTMLDivElement>(null);
  const isHovering = useOnHover(ref);
  const isDragging = useOnDrag(ref);

  const isPlayingPreview = (isHovering || isDragging) && hoverVideo;

  const clickable = !!onClick;

  const renderContent = () => (
    <Stack
      borderRadius='radius-2xl'
      overflow='hidden'
      position='relative'
      className='media-card-container'
      ref={ref}
    >
      <AssetWrapper
        imageProps={image}
        maxWidth={imageMaxWidth}
      />
      {isPlayingPreview && (
        <VideoContainer>
          <AssetWrapper
            cover
            videoProps={{ autoPlay: true, loop: true, muted: true, playsInline: true, ...hoverVideo }}
          />
          {showPlayIcon && (
            <PlayButtonContainer>
              <PlayButtonIcon />
            </PlayButtonContainer>
          )}
        </VideoContainer>
      )}
      {(title || supportingText) && (
        <OverlayTextBlock
          position='absolute'
          padding='spacing-xl'
        >
          <Stack
            borderRadius='radius-2xl'
            background='bg-primary'
            padding='spacing-xl'
            className='overlay-text-block-container'
            direction='column'
            gap={textGap}
          >
            {title && <Typography {...title} />}
            {supportingText && <Typography {...supportingText} />}
          </Stack>
        </OverlayTextBlock>
      )}
    </Stack>
  );

  if (clickable) {
    return <Clickable onClick={onClick}>{renderContent()}</Clickable>;
  }

  return renderContent();
};

export default MediaCard;
